// ABOUTME: Types for the catalog configuration editor (country×business-line enablement matrix + global metadata).
// ABOUTME: Mirrors the tuc-configs BusinessLineConfig / GlobalConfigurationOptions shapes (HCP-111556).

export type CatalogScopeLevel =
  | 'root'
  | 'orgUnit'
  | 'dispatchGroup'
  | 'dispatchCenter'
  | 'plant'
  | 'customer'

export const CATALOG_SCOPE_LEVELS: CatalogScopeLevel[] = [
  'root',
  'orgUnit',
  'dispatchGroup',
  'dispatchCenter',
  'plant',
  'customer',
]

export interface UomEntry {
  id: string
}

/** A supported unit of measure from the backend registry (read-only). */
export interface UnitOfMeasureRef {
  id: string
  name: string
  displaySymbol: string
}

export interface LoadSpacingTruckScenario {
  timeOnJobSite: boolean
  spacing: boolean
  flowRate: boolean
}

export interface LoadSpacingScenarios {
  singleTruck: LoadSpacingTruckScenario
  multiTruck: LoadSpacingTruckScenario
}

export interface LoadSpacingUoMs {
  timeOnJobSite: UomEntry[]
  spacing: UomEntry[]
  flowRate: UomEntry[]
}

/** A catalog-eligible entity section. */
export interface CatalogEntitySection {
  isEnabled: boolean
  supportedScopeLevels: CatalogScopeLevel[]
  availableUnitsOfMeasure?: UomEntry[]
  availableDistanceUnitsOfMeasure?: UomEntry[]
  availableFields?: string[]
  allowedValues?: string[]
  fullLoadsAvailable?: boolean
  loadBalanceAvailable?: boolean
  truckTypeSelectable?: boolean | null
  stockpilingEnabled?: boolean
  scenarios?: LoadSpacingScenarios
  availableUoMs?: LoadSpacingUoMs
}

export interface PresentationFlag {
  isEnabled: boolean
}

export interface PoNumberConfig {
  enabled: boolean
  minLength: number
  maxLength: number
}

export interface BusinessLineConfig {
  quantity: CatalogEntitySection
  retarder: CatalogEntitySection
  loadSpacing: CatalogEntitySection
  trucks: CatalogEntitySection
  unloadingMethod: CatalogEntitySection
  orderChangeLeadTime: CatalogEntitySection
  orderCancellationLeadTime: CatalogEntitySection
  orderIntakeLeadTime: CatalogEntitySection
  operationalHours: CatalogEntitySection
  deliveryWindow: CatalogEntitySection
  pumpSettings: CatalogEntitySection
  overbookingAllowance: CatalogEntitySection
  slotManagementLeadTime?: CatalogEntitySection | null
  testing: PresentationFlag
  notes: PresentationFlag
  concreteUsage: PresentationFlag
  driverInstructions: PresentationFlag
  timeOnJobSite: PresentationFlag
  quantityChangeExpected: PresentationFlag
  poNumber: PoNumberConfig
}

export interface CountryConfigSummary {
  countryCode: string
  businessLine: string
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

/** Catalog entity keys rendered as toggle-able sections, with display labels. */
export const CATALOG_ENTITIES: { key: CatalogEntityKey; label: string }[] = [
  { key: 'quantity', label: 'Quantity' },
  { key: 'retarder', label: 'Retarder' },
  { key: 'loadSpacing', label: 'Load spacing / Delivery rate' },
  { key: 'trucks', label: 'Trucks' },
  { key: 'unloadingMethod', label: 'Unloading method' },
  { key: 'orderChangeLeadTime', label: 'Order change lead time' },
  { key: 'orderCancellationLeadTime', label: 'Order cancellation lead time' },
  { key: 'orderIntakeLeadTime', label: 'Order intake lead time' },
  { key: 'operationalHours', label: 'Operational hours' },
  { key: 'deliveryWindow', label: 'Delivery window' },
  { key: 'pumpSettings', label: 'Pump settings' },
  { key: 'overbookingAllowance', label: 'Overbooking allowance' },
  { key: 'slotManagementLeadTime', label: 'Slot management lead time' },
]

export type CatalogEntityKey =
  | 'quantity'
  | 'retarder'
  | 'loadSpacing'
  | 'trucks'
  | 'unloadingMethod'
  | 'orderChangeLeadTime'
  | 'orderCancellationLeadTime'
  | 'orderIntakeLeadTime'
  | 'operationalHours'
  | 'deliveryWindow'
  | 'pumpSettings'
  | 'overbookingAllowance'
  | 'slotManagementLeadTime'

export const PRESENTATION_FLAGS: { key: PresentationFlagKey; label: string }[] = [
  { key: 'testing', label: 'Testing' },
  { key: 'notes', label: 'Notes' },
  { key: 'concreteUsage', label: 'Concrete usage' },
  { key: 'driverInstructions', label: 'Driver instructions' },
  { key: 'timeOnJobSite', label: 'Time on job site' },
  { key: 'quantityChangeExpected', label: 'Quantity change expected (RMC only)' },
]

export type PresentationFlagKey =
  | 'testing'
  | 'notes'
  | 'concreteUsage'
  | 'driverInstructions'
  | 'timeOnJobSite'
  | 'quantityChangeExpected'

export const CATALOG_BUSINESS_LINES = ['CEM', 'RMC', 'AGG'] as const
export type CatalogBusinessLine = (typeof CATALOG_BUSINESS_LINES)[number]

/** Editable order-change field codes with labels (the fields configurable for online order editing). */
export const ORDER_CHANGE_FIELDS: {code: string; label: string}[] = [
  {code: 'orderConfirmation', label: 'Order confirmation'},
  {code: 'materialChange', label: 'Material change'},
  {code: 'quantity', label: 'Quantity'},
  {code: 'plusLoad', label: 'Plus load'},
  {code: 'maxLoadSize', label: 'Max load size'},
  {code: 'deliveryRate', label: 'Delivery rate'},
  {code: 'flowRate', label: 'Flow rate'},
  {code: 'timeOnJobSite', label: 'Time on job site'},
  {code: 'deliveryDate', label: 'Delivery date'},
  {code: 'latestDeliveryTime', label: 'Latest delivery time'},
  {code: 'isTestBooked', label: 'Is test booked'},
  {code: 'siteContact', label: 'Site contact'},
  {code: 'poNumber', label: 'PO number'},
  {code: 'changeNotes', label: 'Change notes'},
]

/** SAP unloading-method codes with human-readable labels (source: orderrequests-configurations-ui). */
export const UNLOADING_METHODS: {code: string; label: string}[] = [
  {code: '1', label: 'Stationary Customer'},
  {code: '2', label: 'Site Silo'},
  {code: '3', label: 'Site Spreader'},
  {code: '4', label: 'Foil Tube'},
  {code: '5', label: 'Canon Discharge'},
  {code: '6', label: 'Opencast Mining'},
  {code: '7', label: 'Individual Value'},
  {code: '8', label: 'Riser'},
  {code: '9', label: 'Building Chemestry'},
  {code: 'A', label: 'Tipping'},
  {code: 'B', label: 'Bucket'},
  {code: 'C', label: 'Continuous Discharge'},
  {code: 'E', label: 'Third Party Pump'},
  {code: 'G', label: 'Wheel Loader'},
  {code: 'H', label: 'Fundation Pile'},
  {code: 'I', label: 'Screed Pump'},
  {code: 'J', label: 'Curbstone'},
  {code: 'K', label: 'Crane'},
  {code: 'M', label: 'Pumi'},
  {code: 'O', label: 'Own Contracted Pump'},
  {code: 'P', label: 'Customer Pump'},
  {code: 'Q', label: 'Pump Only'},
  {code: 'R', label: 'Pipe'},
  {code: 'S', label: 'Stamix'},
  {code: 'U', label: 'Unknown'},
  {code: 'V', label: 'Paver'},
  {code: 'W', label: 'Wheelbarrow'},
  {code: 'X', label: 'Converabelt'},
]

const emptySection = (): CatalogEntitySection => ({isEnabled: false, supportedScopeLevels: []})

/** A blank configuration used when creating a new country×business-line entry. */
export const emptyBusinessLineConfig = (): BusinessLineConfig => ({
  quantity: emptySection(),
  retarder: emptySection(),
  loadSpacing: emptySection(),
  trucks: emptySection(),
  unloadingMethod: emptySection(),
  orderChangeLeadTime: emptySection(),
  orderCancellationLeadTime: emptySection(),
  orderIntakeLeadTime: emptySection(),
  operationalHours: emptySection(),
  deliveryWindow: emptySection(),
  pumpSettings: emptySection(),
  overbookingAllowance: emptySection(),
  slotManagementLeadTime: null,
  testing: {isEnabled: false},
  notes: {isEnabled: false},
  concreteUsage: {isEnabled: false},
  driverInstructions: {isEnabled: false},
  timeOnJobSite: {isEnabled: false},
  quantityChangeExpected: {isEnabled: false},
  poNumber: {enabled: false, minLength: 0, maxLength: 0},
})

// ── Global (cross-country) catalog metadata ──────────────────────────────────

export interface GlobalEntityMetadata {
  displayName: string
  description: string
  availableReferenceEvents?: string[]
  availableUnitsOfMeasure?: UomEntry[]
  availableCalculationMethods?: string[]
  availableDistanceUnitsOfMeasure?: UomEntry[]
  availableLeadTimeUnitsOfMeasure?: UomEntry[]
  availableSizeUnitsOfMeasure?: UomEntry[]
  availableHoseUnitsOfMeasure?: UomEntry[]
}

export interface GlobalConfigurationOptions {
  quantity: GlobalEntityMetadata
  retarder: GlobalEntityMetadata
  loadSpacing: GlobalEntityMetadata
  trucks: GlobalEntityMetadata
  unloadingMethod: GlobalEntityMetadata
  orderChangeLeadTime: GlobalEntityMetadata
  orderIntakeLeadTime: GlobalEntityMetadata
  orderCancellationLeadTime: GlobalEntityMetadata
  pumpSettings: GlobalEntityMetadata
  deliveryWindow: GlobalEntityMetadata
  operationalHours: GlobalEntityMetadata
  overbookingAllowance: GlobalEntityMetadata
  slotManagementLeadTime: GlobalEntityMetadata
}

export type GlobalEntityKey = keyof GlobalConfigurationOptions

/** Which optional list fields a global entity exposes (the rest are DisplayName/Description only). */
export interface GlobalEntityFieldSpec {
  referenceEvents?: boolean
  unitsOfMeasure?: boolean
  calculationMethods?: boolean
  distanceUnitsOfMeasure?: boolean
  leadTimeUnitsOfMeasure?: boolean
  sizeUnitsOfMeasure?: boolean
  hoseUnitsOfMeasure?: boolean
}

export const GLOBAL_ENTITIES: {key: GlobalEntityKey; label: string; fields: GlobalEntityFieldSpec}[] = [
  {key: 'quantity', label: 'Quantity', fields: {}},
  {key: 'retarder', label: 'Retarder', fields: {}},
  {key: 'loadSpacing', label: 'Load spacing / Delivery rate', fields: {}},
  {key: 'trucks', label: 'Trucks', fields: {}},
  {key: 'unloadingMethod', label: 'Unloading method', fields: {}},
  {key: 'deliveryWindow', label: 'Delivery window', fields: {}},
  {key: 'operationalHours', label: 'Operational hours', fields: {}},
  {
    key: 'orderChangeLeadTime',
    label: 'Order change lead time',
    fields: {referenceEvents: true, unitsOfMeasure: true},
  },
  {
    key: 'orderIntakeLeadTime',
    label: 'Order intake lead time',
    fields: {referenceEvents: true, unitsOfMeasure: true, calculationMethods: true, distanceUnitsOfMeasure: true},
  },
  {
    key: 'orderCancellationLeadTime',
    label: 'Order cancellation lead time',
    fields: {referenceEvents: true, unitsOfMeasure: true, calculationMethods: true, distanceUnitsOfMeasure: true},
  },
  {
    key: 'pumpSettings',
    label: 'Pump settings',
    fields: {referenceEvents: true, leadTimeUnitsOfMeasure: true, sizeUnitsOfMeasure: true, hoseUnitsOfMeasure: true},
  },
  {
    key: 'overbookingAllowance',
    label: 'Overbooking allowance',
    fields: {referenceEvents: true, unitsOfMeasure: true},
  },
  {
    key: 'slotManagementLeadTime',
    label: 'Slot management lead time',
    fields: {referenceEvents: true, unitsOfMeasure: true},
  },
]
